import clsx from 'clsx';
import React, { useEffect, useRef, useState } from 'react';
import moment from 'moment';
import { useReactToPrint } from 'react-to-print';
import { useAppDispatch, useAppSelector } from '../../redux/hooks';
import {
  selectProduct,
  selectSelectedShippingPriority,
  selectStoreData,
  setSelectedShippingPriority
} from '../../redux/slices/customizer/customizerSlice';
import { ITab, ITabs } from '../../types/customizer';
import MyGallery from '../MyGallery';
import If from '../If';
import './index.scss';
import { AnyObject } from '../../utils/basic';
import PrintSummary from '../PrintSummary';
import { getSelectedInfo } from '../../utils/commonRingUtils';
import axios from 'axios';
import { toast } from 'react-toastify';
import Accordion from '../AccordionNew';
import { ReactComponent as QRIcon } from '../../assets/images/qr_icon.svg';
import QRCode from 'react-qr-code';
import URLs from '../../utils/url';
import { useLocation } from 'react-router-dom';

type SummaryProps = {
  tabs: ITabs;
  isPDP?: boolean;
  pathPrefix?: string;
  navigateToUrl: (url: string, replace?: boolean) => void;
  onChange: (updated: any) => void;
};

export const formatSummaryDate = (date: string | undefined) => {
  if (!date) return '';
  const momentDate = moment(date);
  const formatted = momentDate.format('ddd. MMM. Do*');

  if (momentDate.isSame(moment(), 'day')) {
    return `Today (${formatted})`;
  } else if (momentDate.isSame(moment().add(1, 'day'), 'day')) {
    return `Tomorrow (${formatted})`;
  }
  return formatted; // Default format
};

export const getLastestShippingDate = (shippingOptions: AnyObject) => {
  const dates = Object.values(shippingOptions || {}).filter((date) => moment(date, 'YYYY-MM-DD', true).isValid());
  const earliestDate = moment.min(dates.map((date) => moment(date)));
  return earliestDate.format('ddd. MMM. Do*');
};

export const REQUIRED_TAB_FIELDS: Array<string> = []; // 'ringSize', 'engraving' removed from required tabs
export const TABS_TO_AVOID = ['summary', 'data', 'none', 'placement'];

export const getRowContent = (tab: ITab, index: number, options: { [key: string]: boolean } = {}) => {
  const selectedOption = getSelectedInfo(tab);
  const selectedText = selectedOption?.text || '';
  let title = typeof tab.title === 'string' ? tab.title : typeof tab.title === 'function' && tab.title();

  if (tab.eventKey === 'shape') {
    const filteredTabs = tab.items?.filter((item) => !TABS_TO_AVOID.includes(item.eventKey)) || [];
    return (
      <>
        {filteredTabs.map((item, index) => (
          <div className="tw-mt-1" key={index}>
            <p>{item.label || ''}</p>
            <p className="tw-justify-self-end">
              {item.content?.selectedOption?.short_name || item.content?.selectedOption?.name}
            </p>
          </div>
        ))}
      </>
    );
  }

  if (tab.eventKey === 'finish' && selectedText.includes(' - ')) {
    const [firstFinish, secondFinish] = selectedText.split(' - ');
    return (
      <>
        <div className="tw-mt-1">
          <p>First Finish</p>
          <p className="tw-justify-self-end">{firstFinish}</p>
        </div>
        <div className="tw-mt-1">
          <p>Second Finish</p>
          <p className="tw-justify-self-end">{secondFinish}</p>
        </div>
      </>
    );
  }

  if (['outsideFeature_1', 'outsideFeature_2'].includes(tab.eventKey)) {
    if (tab.eventKey === 'outsideFeature_2') {
      title = 'Secondary Outside Feature';
    }

    const filteredTabs = tab.items?.filter((item) => !TABS_TO_AVOID.includes(item.eventKey)) || [];
    return (
      <>
        <div className="tw-mt-3">
          <p>{title}</p>
          <p className="tw-justify-self-end">{tab.selectedFeature || 'None'}</p>
        </div>
        {filteredTabs.map((item, index) => (
          <div key={index}>
            <p>{item.label || ''}</p>
            {(item.eventKey == 'cerakote' && selectedOption?.url?.includes('CER-')) ||
            item.content?.selectedOption?.sku?.includes('CER-') ? (
              <p className="tw-justify-self-end">
                Cerakote{' - '}
                {(item.content?.selectedOption?.name || 'None').replace(/(\d+)mm/g, '$1 mm')}
              </p>
            ) : (
              <p className="tw-justify-self-end">
                {(item.content?.selectedOption?.name || 'None').replace(/(\d+)mm/g, '$1 mm')}
              </p>
            )}
          </div>
        ))}
      </>
    );
  }

  if (tab.eventKey === 'insideFeature') {
    return (
      <div className="tw-mt-3">
        <p>{title}</p>
        <p className="tw-justify-self-end">
          {tab.content?.selectedOption?.sku?.includes('CER-') ? 'Cerakote - ' : ''}
          {selectedText}
        </p>
      </div>
    );
  }

  if (tab.eventKey === 'logo') {
    const [school, logo, accentColor] = selectedText.split(' - ');
    return (
      <>
        <div className="tw-mt-1">
          <p>School</p>
          <p className="tw-justify-self-end">{school}</p>
        </div>
        <div className="tw-mt-1">
          <p>Logo</p>
          <p className="tw-justify-self-end">{logo}</p>
        </div>
        <div className="tw-mt-1">
          <p>Accent Color</p>
          <p className="tw-justify-self-end">{accentColor}</p>
        </div>
      </>
    );
  }

  return (
    <>
      <div className="tw-mt-1">
        <p>{title}</p>
        <p className="tw-justify-self-end">{selectedText}</p>
      </div>

      {tab.eventKey === 'engraving' && selectedOption?.engravingText && (
        <div className="tw-mt-1">
          <p>Engraving Text</p>
          <p className="tw-justify-self-end">{selectedOption.engravingText}</p>
        </div>
      )}
    </>
  );
};

const Summary = ({ tabs, isPDP = false, pathPrefix, navigateToUrl, onChange }: SummaryProps) => {
  const product = useAppSelector(selectProduct);
  const storeData = useAppSelector(selectStoreData);
  const location = useLocation();
  const selectedShippingPriority = useAppSelector(selectSelectedShippingPriority);
  const dispatch = useAppDispatch();
  const componentRef = useRef<HTMLDivElement | null>(null);
  const shipDateRef = useRef<HTMLDivElement | null>(null);
  const [value, setValue] = useState('');
  const [isTouchscreen, setIsTouchscreen] = useState(false);

  useEffect(() => {
    const isWindows = navigator.userAgent.includes('Windows');
    const isDesktopTouch = window.matchMedia('(pointer: coarse) and (min-width: 1024px)').matches;
    setIsTouchscreen(isWindows && isDesktopTouch);
  }, []);

  const triggerShipDatePulse = () => {
    const el = shipDateRef.current;
    if (!el) return;
    el.classList.remove('animate-pulse-once');
    void el.offsetWidth;
    el.classList.add('animate-pulse-once');
    const onEnd = () => {
      el.classList.remove('animate-pulse-once');
      el.removeEventListener('animationend', onEnd);
    };
    el.addEventListener('animationend', onEnd);
  };

  useEffect(() => {
    if ((window as any).__highlightShipDate) {
      (window as any).__highlightShipDate = false;
      triggerShipDatePulse();
    }
  });

  useEffect(() => {
    const handleHighlight = () => triggerShipDatePulse();
    window.addEventListener('highlight-ship-date', handleHighlight);
    return () => window.removeEventListener('highlight-ship-date', handleHighlight);
  }, []);

  /**
   * Set the selected shipping priority to the first available priority
   * only if it is not rush
   */
  useEffect(() => {
    if (!product?.shipping_options) return;

    if (product?.shipping_priority === 'rush') {
      dispatch(setSelectedShippingPriority('rush'));
    } else {
      setDefaultShippingPriority(product?.shipping_options);
    }
  }, [product]);

  const setDefaultShippingPriority = (shippingOptions: any = {}) => {
    // When same_day and next_day are both present, the UI renders them as a single 'Standard' row
    if (shippingOptions.same_day && shippingOptions.next_day) {
      dispatch(setSelectedShippingPriority('standard'));
      return;
    }
    const availablePriority =
      ['standard', 'same_day', 'next_day', 'rush'].find((priorityKey) => !!shippingOptions?.[priorityKey]) || '';
    dispatch(setSelectedShippingPriority(availablePriority));
  };

  const handlePrint = useReactToPrint({
    contentRef: componentRef,
    documentTitle: 'Summary',
    bodyClass: 'print-window'
  });

  const handleSearch = async () => {
    try {
      const response: any = await axios.get(
        `${process.env.REACT_APP_API_BASE_URL}/v1/rings/searchMagicNumber?magicNumber=${value}`
      );
      const customizeUrl = response.data.ring_data?.customize_url;
      const bestseller_id = response.data.ring_data?.bestseller_id;
      if (bestseller_id) {
        // Add bestseller parameter to the URL
        window.location.href += `&bestSeller=${bestseller_id}`;
      } else if (customizeUrl) {
        navigateToUrl(customizeUrl);
        setValue('');
      } else {
        toast.error(response.data.message?.replace('Magic Number', 'Reference Code'), { className: 'toast-error' });
      }
    } catch (error) {
      console.log(error);
    }
  };

  const handleShare = async () => {
    try {
      const link = `${URLs.siteBaseUrl}${location.pathname}${location.search}`;
      // const image_link = `${URLs.assetsBaseUrl}${images[0]}`;
      const body = {
        link
        // image_link
      };
      const response = await axios.post(`${URLs.apiBaseUrl}/v2/shareStyle`, body);
      toast.success('Style shared successfully!', { className: 'toast-success' });
    } catch (error: any) {
      console.error('Error sharing style:', error);
      const errorMessage =
        error?.response?.data?.message || error?.message || 'Failed to share style. Please try again.';
      toast.error(errorMessage, { className: 'toast-error' });
    }
  };

  const getRingInfo = () => {
    return (
      <>
        <div className={clsx('summary-container tw-mt-3 tw-mr-3 tw-mb-3')}>
          {REQUIRED_TABS.map((tab: ITab, index) => (
            <React.Fragment key={index}> {getRowContent(tab, index, { isPDP })}</React.Fragment>
          ))}
        </div>
        <div className={clsx('summary-container tw-mt-3 tw-mr-3 tw-mb-3')}>
          {ADDITIONAL_TABS.map((tab: ITab, index) => (
            <React.Fragment key={index}> {getRowContent(tab, index, { isPDP })}</React.Fragment>
          ))}
        </div>
      </>
    );
  };

  const containerClass = clsx({
    'tw-overflow-y-auto tw-overflow-x-hidden tw-h-full tw-py-5 lg:tw-pt-20 lg:tw-pb-0 tw-px-5 swatch-filter-border':
      !isPDP,
    'tw-w-full tw-h-full tw-py-1 tw-pb-4 tw-bg-blue-50 tw-text-xs summary-pdp': isPDP
  });

  const flexWrapperClass = clsx('tw-flex tw-flex-wrap summary tw-text-base', {
    'xl:tw-w-11/12 tw-ml-auto': !isPDP
  });

  const innerContainerClass = clsx('tw-text-prussian-blue', {
    'tw-w-full lg:tw-w-3/5 lg:tw-pr-8 xl:tw-w-2/3 xl:tw-pr-[15%] 2xl:tw-pr-[20%]': !isPDP,
    'tw-w-full': isPDP
  });

  const REQUIRED_TABS = tabs.filter((tab: ITab) => REQUIRED_TAB_FIELDS.includes(tab.eventKey));
  const ADDITIONAL_TABS = tabs.filter(
    (tab: ITab) => !REQUIRED_TAB_FIELDS.includes(tab.eventKey) && !TABS_TO_AVOID.includes(tab.eventKey)
  );

  /**
   * If the shipping priority is rush, add the shipping priority to the url.
   * If the shipping priority is not rush, remove the shipping priority from the url.
   */
  const updateShippingPriority = (priority: string) => {
    dispatch(setSelectedShippingPriority(priority));
    if (priority === 'rush') {
      onChange({ shipping_priority: 'rush' });
    } else {
      onChange({ shipping_priority: undefined });
    }
  };

  return (
    <div className={containerClass}>
      <div className={flexWrapperClass}>
        <div className={innerContainerClass}>
          {/* Search Reference Code */}
          <div className="tw-mt-4 tw-flex  xs:tw-flex-col sm:tw-flex-row tw-gap-4">
            <input
              type="text"
              name="magic_number"
              placeholder="000-03C9-10"
              value={value}
              onChange={(e) => {
                const formatted = e.target.value.replace(/O/gi, '0');
                setValue(formatted);
              }}
              className="tw-text-sm tw-p-3 tw-border tw-mr-3 xs:!tw-w-50 sm:!tw-w-40 xs:tw-m-auto sm:tw-m-0 tw-rounded-none"
            />
            <button
              id="search-btn"
              className="tw-btn tw-btn-brand tw-btn-sm tw-w-50 xs-tw-m-auto sm:tw-m-0 tw-rounded-none"
              onClick={handleSearch}
            >
              Search Reference Code
            </button>
          </div>

          {/* SHIP DATE SECTION */}
          <If condition={!!storeData?.can_select_shipping_priority}>
            <div ref={shipDateRef} className="tw-border tw-border-solid tw-rounded-none tw-p-6 tw-mt-6">
              <div className="tw-text-lg tw-font-bold tw-pb-3">Manufacturing Rush</div>
              <div className="summary-container">
                {product?.shipping_options?.same_day && product?.shipping_options?.next_day ? (
                  <div className="tw-mt-1">
                    <p className="tw-font-medium">Standard</p>
                    <label className="tw-justify-self-end tw-flex tw-items-center tw-gap-2 tw-cursor-pointer">
                      {formatSummaryDate(
                        moment(product?.shipping_options?.same_day).isBefore(
                          moment(product?.shipping_options?.next_day)
                        )
                          ? product?.shipping_options?.same_day
                          : product?.shipping_options?.next_day
                      )}
                      <input
                        className="shipping-radio"
                        type="radio"
                        name="shipping-priority"
                        checked={selectedShippingPriority === 'standard'}
                        onChange={() => updateShippingPriority('standard')}
                      />
                    </label>
                  </div>
                ) : (
                  <>
                    {product?.shipping_options?.same_day ? (
                      <div className="tw-mt-1">
                        <p className="tw-font-medium">Same Day</p>
                        <label className="tw-justify-self-end tw-flex tw-items-center tw-gap-2 tw-cursor-pointer">
                          {formatSummaryDate(product?.shipping_options?.same_day)}
                          <input
                            type="radio"
                            name="shipping-priority"
                            checked={selectedShippingPriority === 'same_day'}
                            onChange={() => updateShippingPriority('same_day')}
                          />
                        </label>
                      </div>
                    ) : null}
                    {product?.shipping_options?.next_day ? (
                      <div className="tw-mt-1">
                        <p className="tw-font-medium">Next Day</p>
                        <label className="tw-justify-self-end tw-flex tw-items-center tw-gap-2 tw-cursor-pointer">
                          {formatSummaryDate(product?.shipping_options?.next_day)}
                          <input
                            type="radio"
                            name="shipping-priority"
                            checked={selectedShippingPriority === 'next_day'}
                            onChange={() => updateShippingPriority('next_day')}
                          />
                        </label>
                      </div>
                    ) : null}
                  </>
                )}
                {product?.shipping_options?.standard ? (
                  <div className="tw-mt-1">
                    <p className="tw-font-medium">Without Rush</p>
                    <label className="tw-justify-self-end tw-flex tw-items-center tw-gap-2 tw-cursor-pointer">
                      {formatSummaryDate(product?.shipping_options?.standard)}
                      <input
                        className="shipping-radio"
                        type="radio"
                        name="shipping-priority"
                        checked={selectedShippingPriority === 'standard'}
                        onChange={() => updateShippingPriority('standard')}
                      />
                    </label>
                  </div>
                ) : null}
                {product?.shipping_options?.rush ? (
                  <div className="tw-mt-1">
                    <p className="tw-font-medium">
                      With Rush{' '}
                      {product?.shipping_options?.rush_fee ? `(+ $${product?.shipping_options?.rush_fee})` : ''}
                    </p>
                    <label className="tw-justify-self-end tw-flex tw-items-center tw-gap-2 tw-cursor-pointer">
                      {formatSummaryDate(product?.shipping_options?.rush)}
                      <input
                        className="shipping-radio"
                        type="radio"
                        name="shipping-priority"
                        checked={selectedShippingPriority === 'rush'}
                        onChange={() => updateShippingPriority('rush')}
                      />
                    </label>
                  </div>
                ) : null}
              </div>
              <div className="tw-pt-5 tw-text-xs">
                {product?.shipping_options?.rush
                  ? '*Every ring is made to order. By selecting "Rush", we expedite the manufacturing of your ring. If you need a rush sooner than the dates displayed, contact Solution Point.'
                  : '*Every ring is made to order. If you need a rush sooner than the date displayed, contact Solution Point.'}
              </div>
            </div>
          </If>

          {/* INFORMATION FOR ORDERING */}
          <div className="tw-border tw-border-solid tw-rounded-none tw-mt-4 tw-pb-5">
            <div className="tw-text-lg tw-font-bold tw-px-6 tw-pt-5 tw-pb-3">Information for Ordering</div>
            <div className="summary-container">
              <div className="tw-flex tw-my-2 tw-px-6">
                <p className="tw-w-40 tw-shrink-0 tw-mr-3">Ring Category Sku</p>
                <p className="tw-justify-start">{product?.helzberg_custom_sku}</p>
              </div>
              <div className="tw-flex tw-my-2 tw-px-6">
                <p className="tw-w-40 tw-shrink-0 tw-mr-3">Reference Code</p>
                <p className="tw-justify-start">{product?.magic_number}</p>
              </div>
              <div className="tw-flex tw-my-2 tw-px-6">
                <p className="tw-w-40 tw-shrink-0 tw-mr-3">Description</p>
                <p className="tw-justify-start tw-whitespace-pre-line">{product?.description}</p>
              </div>
            </div>
          </div>

          {/* RING INFO */}
          <Accordion
            title="See Ring Details"
            className="summary-accordian tw-mt-4 tw-border tw-border-solid tw-border-blue-900 tw-rounded-none"
          >
            {getRingInfo()}
          </Accordion>

          <>
            <button
              id="print-btn"
              className="tw-btn tw-btn-brand tw-p-3 tw-w-40 tw-my-6 tw-rounded-none"
              onClick={handlePrint}
            >
              Print
            </button>
            <PrintSummary tabs={tabs} image={product?.images?.[0]} pathPrefix={pathPrefix} ref={componentRef} />
          </>
        </div>
        {!!product?.images?.length && (
          <div className="tw-flex-1 gallery_container tw-relative tw-hidden lg:tw-flex tw-flex-col">
            <MyGallery images={product?.images} />
            {/* QR Code Sharing Section */}
            <div className="tw-mt-6 xl:tw-w-3/4 tw-m-auto">
              {/* Top Section: Icon/Text and QR Code */}
              <div className="tw-flex tw-items-center tw-justify-between tw-gap-1">
                {/* Selfie Icon and Text - Centered */}
                <div className="tw-flex tw-flex-col tw-items-center tw-justify-center tw-gap-2 tw-flex-1">
                  <QRIcon className="tw-w-20 tw-h-20" style={{ color: 'rgb(28, 26, 26)' }} />
                  <span
                    className="tw-text-sm tw-italic tw-text-center tw-leading-tight"
                    style={{ color: 'rgb(28, 26, 26)' }}
                  >
                    Scan to take your design with you
                  </span>
                </div>

                {/* QR Code - Far Right */}
                <div className="tw-flex tw-justify-end tw-flex-shrink-0">
                  {(() => {
                    const isStagingApi = (process.env.REACT_APP_API_BASE_URL || '').includes('staging');
                    const HELZBERG_ORIGIN = isStagingApi
                      ? 'https://bhdw-005.dx.commercecloud.salesforce.com'
                      : 'https://www.helzberg.com';
                    const HELZBERG_BUILDER_PATH =
                      '/on/demandware.store/Sites-Helzberg-Site/en_US/Product-ProductBuilder';
                    let qrUrl: string;
                    const redirectUrl = new URLSearchParams(location.search).get('redirect_url');
                    if (redirectUrl) {
                      let origin = URLs.siteBaseUrl;
                      try {
                        origin = new URL(redirectUrl).origin;
                      } catch {
                        // redirect_url malformed; fall back to siteBaseUrl
                      }
                      qrUrl = `${origin}${location.pathname}${location.search}`;
                    } else if (pathPrefix === 'helzberg') {
                      const params = new URLSearchParams(location.search);
                      if (params.has('ring_url')) {
                        // Already on the Helzberg ProductBuilder URL (ring path lives in the
                        // ring_url param); just retarget to the correct origin instead of
                        // re-wrapping the whole URL into another ring_url param.
                        qrUrl = `${HELZBERG_ORIGIN}${location.pathname}${location.search}`;
                      } else {
                        const ringUrl = encodeURIComponent(`${location.pathname}${location.search}`);
                        qrUrl = `${HELZBERG_ORIGIN}${HELZBERG_BUILDER_PATH}?ring_url=${ringUrl}`;
                      }
                    } else {
                      qrUrl = `${URLs.siteBaseUrl}${location.pathname}${location.search}`;
                    }
                    console.log('[QR Code URL]', qrUrl);
                    return <QRCode value={qrUrl} size={140} fgColor="rgb(28, 26, 26)" />;
                  })()}
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default Summary;
